import apiClient from './axios.config';
import { API_ENDPOINTS } from '../../constants';

class DocumentsAPI {
    /**
     * Upload a document
     * @param {File} file - File to upload
     * @param {Object} metadata - Additional metadata
     * @returns {Promise<Object>} - Upload result
     */
    async upload(file, metadata = {}) {
        try {
            const formData = new FormData();
            formData.append('file', file);

            // Add metadata
            Object.keys(metadata).forEach(key => {
                formData.append(key, metadata[key]);
            });

            const response = await apiClient.post(API_ENDPOINTS.DOCUMENTS_UPLOAD, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    console.log(`Upload progress: ${progress}%`);
                },
            });

            return response.data;
        } catch (error) {
            console.error('Upload error:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to upload file');
        }
    }

    /**
     * Get document by ID
     * @param {number} id - Document ID
     * @returns {Promise<Object>} - Document data
     */
    async getById(id) {
        try {
            const response = await apiClient.get(`${API_ENDPOINTS.DOCUMENTS}/${id}`);
            return response.data;
        } catch (error) {
            console.error('Get document error:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to get document');
        }
    }

    /**
     * Download document
     * @param {number} id - Document ID
     * @returns {Promise<Blob>} - File blob
     */
    async download(id) {
        try {
            const response = await apiClient.get(API_ENDPOINTS.DOCUMENTS_DOWNLOAD(id), {
                responseType: 'blob',
            });
            return response.data;
        } catch (error) {
            console.error('Download error:', error);
            // When responseType is 'blob', error bodies come back as Blobs — parse them first
            if (error.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const json = JSON.parse(text);
                    const status = error.response.status;
                    if (status === 404) throw new Error('Receipt file is no longer available on the server. Please re-upload the receipt.');
                    if (status === 403) throw new Error('You do not have permission to download this file.');
                    throw new Error(json.message || 'Failed to download file');
                } catch (parseErr) {
                    if (parseErr.message !== 'Failed to download file') throw parseErr;
                }
            }
            throw new Error(error.message || 'Failed to download file');
        }
    }

    /**
     * Delete document
     * @param {number} id - Document ID
     * @returns {Promise<Object>} - Delete result
     */
    async delete(id) {
        try {
            const response = await apiClient.delete(`${API_ENDPOINTS.DOCUMENTS}/${id}`);
            return response.data;
        } catch (error) {
            console.error('Delete document error:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to delete document');
        }
    }

    /**
     * Get user's documents
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} - Documents data
     */
    async getMyDocuments(params = {}) {
        try {
            const response = await apiClient.get(API_ENDPOINTS.DOCUMENTS_MY, { params });
            return response.data;
        } catch (error) {
            console.error('Get my documents error:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to get documents');
        }
    }

    /**
     * Get documents for a specific user
     * @param {number} userId - User ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} - User documents data
     */
    async getUserDocuments(userId, params = {}) {
        try {
            const response = await apiClient.get(API_ENDPOINTS.DOCUMENTS_USER(userId), { params });
            return response.data;
        } catch (error) {
            console.error('Get user documents error:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to get user documents');
        }
    }

    /**
     * Check if user has any documents
     * @param {number} userId - User ID
     * @returns {Promise<boolean>} - Whether user has documents
     */
    async hasDocuments(userId) {
        try {
            const response = await this.getUserDocuments(userId, { limit: 1 });
            return response.data && response.data.documents && response.data.documents.length > 0;
        } catch (error) {
            console.error('Check user documents error:', error);
            // If we get a permission error, assume user has no documents
            if (error.message.includes('permission')) {
                return false;
            }
            throw new Error(error.response?.data?.message || error.message || 'Failed to check user documents');
        }
    }
}

// Create and export a singleton instance
const documentsAPI = new DocumentsAPI();
export { documentsAPI };

// Also export the class for potential custom instances
export default DocumentsAPI;